// A deliberately limited, deterministic browser demonstration. No network operations.
const types = {
  schedule: { label: "Schedule", icon: "◷", description: "Weekdays at 09:00" },
  source: {
    label: "Search Console",
    icon: "↗",
    description: "Compare complete 7-day periods",
  },
  condition: {
    label: "Condition",
    icon: "⑂",
    description: "Clicks decrease beyond threshold",
  },
  site: {
    label: "Site check",
    icon: "◎",
    description: "Inspect example pages",
  },
  task: {
    label: "Create task",
    icon: "☑",
    description: "Record the sample finding",
  },
  notify: {
    label: "Notify me",
    icon: "◉",
    description: "A useful update at your desk",
  },
};
const allowed = {
  schedule: ["source"],
  source: ["condition"],
  condition: ["site"],
  site: ["site", "task"],
  task: ["task", "notify"],
  notify: ["notify"],
};
function preset(kind = "decline") {
  const positions = [
    [24, 40],
    [286, 40],
    [548, 40],
    [548, 218],
    [286, 218],
    [24, 218],
  ];
  const nodes = Object.keys(types).map((type, i) => ({
    id: `n${i + 1}`,
    type,
    x: positions[i][0],
    y: positions[i][1],
    threshold: 20,
    mode: "desk",
    time: "09:00",
    added: false,
  }));
  return {
    kind,
    nodes,
    edges: nodes
      .slice(0, -1)
      .map((n, i) => ({ from: n.id, to: nodes[i + 1].id })),
  };
}
function canConnect(nodes, from, to) {
  const a = nodes.find((n) => n.id === from),
    b = nodes.find((n) => n.id === to);
  return !!(
    a &&
    b &&
    a.id !== b.id &&
    (allowed[a.type] || []).includes(b.type)
  );
}
function validate(graph) {
  const { nodes, edges } = graph;
  if (nodes.some((n) => !types[n.type]))
    return { error: "This demo cannot execute an unsupported block." };
  if (edges.some((e) => !canConnect(nodes, e.from, e.to)))
    return {
      error:
        "Connect compatible output and input handles. The example runs from Schedule to Notify me.",
    };
  if (
    nodes.some(
      (n) =>
        edges.filter((e) => e.from === n.id).length > 1 ||
        edges.filter((e) => e.to === n.id).length > 1,
    )
  )
    return { error: "Each demo block supports one input and one output." };
  const start = nodes.find((n) => n.type === "schedule");
  if (!start) return { error: "Add a Schedule block to begin." };
  const route = [],
    visited = new Set();
  let node = start;
  while (node) {
    if (visited.has(node.id))
      return {
        error:
          "This demo supports a single route without loops. Disconnect the circular link.",
      };
    visited.add(node.id);
    route.push(node);
    const edge = edges.find((e) => e.from === node.id);
    node = edge ? nodes.find((n) => n.id === edge.to) : null;
  }
  for (const type of ["source", "condition", "site", "task"]) {
    if (!route.some((n) => n.type === type))
      return {
        error: `Incomplete path: connect ${types[type].label} to the route before running.`,
      };
  }
  if (
    route.some(
      (n) =>
        n.type === "condition" &&
        (!Number.isFinite(Number(n.threshold)) ||
          Number(n.threshold) < 1 ||
          Number(n.threshold) > 100),
    )
  )
    return { error: "Choose a threshold between 1% and 100%." };
  if (
    route.some(
      (n) =>
        n.type === "notify" && !["desk", "workspace", "quiet"].includes(n.mode),
    )
  )
    return { error: "Choose a supported notification mode." };
  return { route, detached: nodes.filter((n) => !visited.has(n.id)) };
}
const scenarios = {
  decline: {
    label: "Traffic watch",
    metric: "clicks",
    previous: 1000,
    current: 720,
    higherIsBetter: true,
    source: "Search Console",
  },
  recovery: {
    label: "Recovery check",
    metric: "clicks",
    previous: 720,
    current: 1000,
    higherIsBetter: true,
    source: "Search Console",
    recovery: true,
  },
  growth: {
    label: "Conversion growth",
    metric: "conversions",
    previous: 100,
    current: 128,
    higherIsBetter: true,
    source: "Analytics",
  },
  errors: {
    label: "Error increase",
    metric: "errors",
    previous: 10,
    current: 16,
    higherIsBetter: false,
    source: "Website monitor",
  },
  latency: {
    label: "Slower responses",
    metric: "response time",
    unit: "ms",
    previous: 400,
    current: 560,
    higherIsBetter: false,
    source: "Website monitor",
  },
  ranking: {
    label: "Ranking improvement",
    metric: "ranking position",
    previous: 10,
    current: 6,
    higherIsBetter: false,
    source: "SEO tool",
  },
};
function assessMetric(fixture, threshold) {
  const delta = fixture.current - fixture.previous;
  const magnitude = (Math.abs(delta) / fixture.previous) * 100;
  const change = Math.round(magnitude * 100) / 100;
  const direction =
    delta > 0 ? "increase" : delta < 0 ? "decrease" : "no change";
  const positive =
    delta !== 0 && (fixture.higherIsBetter ? delta > 0 : delta < 0);
  // Compare scaled values so exact 28% is not promoted by floating-point division.
  const meaningful =
    Math.abs(delta) * 100 > fixture.previous * Number(threshold);
  return {
    change,
    direction,
    positive,
    meaningful,
    outcome: meaningful ? (positive ? "positive" : "attention") : "idle",
  };
}
function simulate(graph) {
  const checked = validate(graph);
  if (checked.error) return checked;
  const sample = scenarios[graph.kind || "decline"];
  if (!sample)
    return { error: "Choose one of the supported sample scenarios." };
  const recovery = !!sample.recovery;
  const fixture = { previous: sample.previous, current: sample.current };
  const comparison = assessMetric(
    sample,
    checked.route.find((n) => n.type === "condition").threshold,
  );
  const { change, direction, positive } = comparison;
  const quiet = checked.route.some(
    (n) => n.type === "notify" && n.mode === "quiet",
  );
  let branch = true;
  const summary = `${sample.metric[0].toUpperCase() + sample.metric.slice(1)} ${direction === "increase" ? "rose" : "fell"} by ${change}% in the demo period. ${positive ? "A positive sample change; review the details." : "Here are the pages to investigate."}`;
  const steps = checked.route.map((node) => {
    let message = "",
      status = "complete",
      notification = null;
    if (!branch)
      return {
        id: node.id,
        status: "skipped",
        message: `${types[node.type].label}: skipped; threshold was not exceeded.`,
      };
    switch (node.type) {
      case "schedule":
        message = `Sample weekday run at ${node.time}; no recurring job has been scheduled.`;
        break;
      case "source":
        message = `Sample ${sample.metric}: ${sample.previous.toLocaleString("en")} → ${sample.current.toLocaleString("en")}${sample.unit ? " " + sample.unit : ""}. Latest complete 7 days versus previous 7 days.`;
        break;
      case "condition":
        branch = comparison.meaningful;
        message = `${change}% ${direction} ${branch ? "exceeds" : "does not exceed"} ${node.threshold}%. ${sample.higherIsBetter ? "Higher" : "Lower"} ${sample.metric} is better. ${branch ? (positive ? "Positive sample change." : "Attention needed.") : "No alert needed."}`;
        break;
      case "site":
        message = recovery
          ? "Sample check: example.com/guides and example.com/pricing are responding again (200)."
          : positive
            ? "Sample check: example.com/guides and example.com/pricing are responding (200). This does not establish the cause of the improvement."
            : "Sample check: example.com/guides (500); example.com/pricing (200). Investigate; this does not establish the cause of the change.";
        break;
      case "task":
        message = recovery
          ? "Sample task recorded: review the recovery summary."
          : `Sample task recorded: review the ${sample.metric} ${positive ? "improvement" : "change"} and affected pages.`;
        break;
      case "notify":
        notification =
          node.mode === "quiet"
            ? null
            : recovery
              ? "The monitored pages are responding again. I’ve prepared a recovery summary."
              : summary;
        message =
          node.mode === "quiet"
            ? "Quiet mode: update recorded in the log. No desk notification."
            : `${node.mode === "desk" ? "Desk" : "Workspace"} notification: ${notification}`;
        break;
    }
    return { id: node.id, status, message, notification, mode: node.mode };
  });
  const hasNotification = steps.some((s) => s.notification);
  const reaction = quiet
    ? "quiet"
    : !hasNotification
      ? "idle"
      : recovery
        ? "recovery"
        : comparison.outcome;
  return {
    steps,
    branch,
    recovery,
    quiet,
    reaction,
    positive,
    detached: checked.detached,
    hasNotification,
    change,
    fixture,
    sample,
    comparison,
  };
}
module.exports = {
  types,
  preset,
  canConnect,
  validate,
  simulate,
  scenarios,
  assessMetric,
};
